import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Placement } from "@popperjs/core";
import {
	Badge,
	Button,
	DataObserveKey,
	Divider,
	FocusableComponent,
	Icon,
	IconOptions,
	InlineText,
	Paragraph,
	Spinner,
	TextContainer,
	VisualComponent,
	useTranslations,
	SrOnly,
	useUniqueId,
	cn,
} from "@siteimprove/fancylib";
import { FormControl } from "../form/form";
import { InputField } from "../input-field/input-field";
import { useLabTranslations } from "../../../translations/translations";
import { DropdownButtonProps, Popover } from "../../overlay/popover/popover";
import { ActionBar } from "../../actions-and-controls/action-bar/action-bar";
import { dataObserveKeyDiscriminator } from "../../../utils/shorthands";
import { useReRender } from "../../../utils/hooks";
import { Trigger } from "../../../utils/dom-utils";
import {
	BaseSelectOption,
	BaseSelectOptionProps,
	GroupContainerItem,
	OptionItem,
	OptionProps,
	SelectItem,
	SelectListbox,
	flattenOptions,
	indexOfOption,
	isOptionEqual,
	areOptionsEqual,
	isOptionItem,
	CompareFunction,
	SelectDto,
} from "./select-listbox";
import {
	BaseOverviewOption,
	BaseOverviewOptionProps,
	OverviewOptionProps,
	SelectOverviewList,
} from "./select-overview-list";
import * as scss from "./select.scss";

export { BaseSelectOption, BaseOverviewOption, indexOfOption, isOptionItem, isOptionEqual };
export type {
	SelectItem,
	OptionItem,
	GroupContainerItem,
	OptionProps,
	BaseSelectOptionProps,
	BaseOverviewOptionProps,
	OverviewOptionProps,
};
export type SelectValue<T> = T extends readonly (infer K)[] ? K[] : T | undefined;

export type SelectProps<T> = FormControl<SelectValue<T>> & {
	/** List of options or groups of options */
	items?: SelectItem<T>[];
	/** Function that return true is two items are equal */
	compareFn?: CompareFunction<T>;
	/** Placeholder for the select */
	placeholder?: string;
	/** Popover anchor becomes an icon-only button */
	iconOnly?: boolean;
	/** Should the select be full width? */
	fullWidth?: boolean;
	/** Option selected by default. If undefined, the default is the first option. Use noDefaultOption property to disable it.*/
	defaultOption?: SelectDto<T>;
	/** If true, does not use the default option. */
	noDefaultOption?: boolean;
	/** Can the select button be clicked */
	disabled?: boolean;
	/** Displays a spinner in the listbox */
	loading?: boolean;
	/** Enables searching functionality. If set to auto, search bar is visible only when there are more than 7 items. */
	searchable?: "always" | "auto" | "never";
	/** Handler for searching event */
	onSearch?: (query: string, caseSensitive: boolean) => void;
	/** Enables case sensitive search. */
	caseSensitive?: boolean;
	/** Placeholder for search field */
	searchPlaceholder?: string;
	/** Placeholder for search field */
	searchHelpText?: string | React.ReactNode;
	/** Aria-label for search field */
	searchLabel?: string;
	/** Defines which text content the search should be applied to. Defaults to the options title. */
	searchableContent?: (item: OptionItem<T>) => string;
	/** Enables bulk actions such as select/deselect all */
	bulkActions?: boolean;
	/** Enables creating options when the search doesn't exactly match any options */
	creatable?: boolean;
	/** Callback for onCreate option event */
	onCreate?: (value: string) => OptionItem<T> | false | Promise<OptionItem<T> | false>;
	/** Label to be displayed on the create option button  */
	createButtonLabel?: string;
	/** Hides the button to clear the selection */
	hideClearButton?: boolean;
	/** If enabled, shows selected items on top when opening the listbox. In multi-selection mode, it defaults to true. */
	showSelectedItemsOnTop?: boolean;
	/** Shows selected items in a box bellow the select input */
	showSelectionOverview?: boolean;
	/** Label that describes the overview list */
	overviewLabel?: string;
	/** Role of the list box */
	role?: "listbox" | "menu";
	/** Amount of time in milliseconds used to identify that the user has finished typing a string */
	keyboardTypingInterval?: number;
	/** Maximum number of items that can be selected */
	maxNumberOfItems?: number;
	/** Custom renderer for options */
	optionRenderer?: (props: OptionProps<T> & React.RefAttributes<HTMLDivElement>) => JSX.Element;
	/** Custom renderer for overview options */
	overviewOptionRenderer?: (props: OverviewOptionProps<T>) => JSX.Element;
	/** props to pass down to the button */
	buttonProps?: Omit<DropdownButtonProps, "variant"> & {
		variant?: "primary" | "secondary" | "destructive"; // we don't allow borderless dropdowns
	};
	/** Ref of the button */
	buttonRef?: React.RefObject<HTMLButtonElement>;
	/** Controls the width of the select button. */
	buttonWidth?: number;
	/** Controls the width of the select listbox. */
	listboxWidth?: number;
	/** If true, the default listbox minimum width won't be set */
	noListboxMinWidth?: boolean;
	/** If true, the default listbox maximum width won't be set */
	noListboxMaxWidth?: boolean;
	/** Preferred placement for the listbox */
	placement?: Placement;
	/** Allowed placements for the listbox when using an "auto" value for the "placement" prop */
	allowedAutoPlacements?: Placement[];
	/** Hide the chevron icon */
	hideChevron?: boolean;
	/** Defines how the selected item is rendered in the button. Defaults to the option title. */
	buttonRenderer?:
		| (() => React.ReactNode)
		| (T extends readonly (infer K)[]
				? (selectedOption: OptionItem<K>[]) => React.ReactNode
				: (selectedOption: OptionItem<T> | undefined) => React.ReactNode);
	/** Heading for the listbox */
	listboxHeading?: React.ReactNode;
	/** Custom function to handle selection changes, where the input is a list of options affected and the output is a list of selected options */
	stateManagerBuilder?: (
		currentSelection: OptionItem<T>[],
		multiple: boolean | undefined
	) => SelectStateManager<T>;
} & DataObserveKey &
	VisualComponent &
	FocusableComponent;

export function Select<T>(props: SelectProps<T>): JSX.Element {
	const multiple = Array.isArray(props.value);
	const {
		allowedAutoPlacements,
		bulkActions,
		buttonProps,
		buttonRef = createRef<HTMLButtonElement>(),
		buttonRenderer,
		caseSensitive,
		className,
		compareFn,
		creatable,
		createButtonLabel,
		defaultOption: userDefaultValue,
		disabled,
		fullWidth = props.buttonWidth !== undefined,
		hideChevron,
		hideClearButton,
		iconOnly,
		id,
		invalid,
		items = [],
		listboxHeading,
		listboxWidth,
		loading,
		maxNumberOfItems,
		noDefaultOption,
		noListboxMaxWidth = fullWidth || listboxWidth !== undefined,
		noListboxMinWidth = listboxWidth !== undefined,
		onBlur,
		onChange,
		onCreate,
		onFocus,
		onKeyDown,
		onMouseDown,
		onMouseEnter,
		onMouseLeave,
		onSearch,
		optionRenderer,
		overviewLabel,
		overviewOptionRenderer,
		placeholder,
		placement,
		searchHelpText,
		searchLabel,
		searchPlaceholder,
		searchable = onSearch || creatable || bulkActions ? "always" : "auto",
		searchableContent,
		showSelectedItemsOnTop = multiple,
		showSelectionOverview,
		stateManagerBuilder,
		style,
		tabIndex,
		value,
		buttonWidth,
		...rest
	} = props;
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [filteredItems, searchQueryExactlyMatches] = useFilteredOptions(
		items,
		searchQuery,
		caseSensitive,
		searchableContent,
		onSearch
	);
	const listboxId = useUniqueId("listbox");
	const overviewId = useUniqueId("listbox-overview");
	const i18nLab = useLabTranslations();
	const i18nLib = useTranslations();
	const [innerValue, setInnerValue] = useState(value);
	const [pristine, setPristine] = useState(true);
	const [screenReadFeedback, setScreenReadFeedback] = useState<string>("");
	const flatOptions = useMemo(() => flattenOptions(items), [items]);
	const flatFilteredOptions = useMemo(() => flattenOptions(filteredItems), [filteredItems]);
	const [selectedOptions, allOptions] = useCachedSelection(flatOptions, innerValue, compareFn);
	const [initialSelection, setInitialSelection] = useState<OptionItem<T>[]>(selectedOptions);
	const [lastCreatedOption, setLastCreatedOption] = useState<OptionItem<T>>();
	const [defaultOption, isDefaultOptionSelected] = useDefaultOption(
		props,
		selectedOptions,
		allOptions
	);
	const defaultStateManager = useSelectStateManager(selectedOptions, multiple, compareFn);
	const selectionManager =
		stateManagerBuilder !== undefined
			? stateManagerBuilder(selectedOptions, multiple)
			: defaultStateManager;

	// propagate changes to inner states
	useEffect(() => setInnerValue(value), [value]);

	// when the items list changes and the selection was not changed by the user yet,
	// we need to re-sync the initial selection. It handles cases where the
	// items list is loaded asynchonously and the selection is set before
	// the items are loaded.
	useEffect(() => {
		if (pristine) return setInitialSelection(selectedOptions);
	}, [items]);

	// the last created option should be selected on the next render
	// cycle after its creation because the list of options is not
	// updated immediately and it will not contain the new option yet
	useEffect(() => {
		if (lastCreatedOption) {
			handleOnChange(selectionManager.toggleOption(lastCreatedOption));
			setLastCreatedOption(undefined);
			triggerListboxRender();
		}
	}, [items, lastCreatedOption]);

	// Normally we do not want the listbox-items order to change as the user is selecting and
	// deselecting items - only when eventually accepting the changes shall the listbox items
	// be re-ordered to have the selected at top (the default behavior).
	// Except when adding a new item: in that case we DO want to place the newly added and
	// selected item among the other selected items. But there's nothing specific about that
	// state which can implicitly trigger this behavior: first the item is added (which cause
	// the list-re-ordering but the item isn't selected yet) and only then is the item selected,
	// which intentionally doesn't change the ordering.
	// This is a job for the ReRender util:
	const { dependency: reRenderListbox, triggerRender: triggerListboxRender } = useReRender();

	// the searching functionality is enabled when:
	const isSearchingEnabled =
		// the searchable prop is set to always
		searchable === "always" ||
		// or it's set to auto and the items list has at least 7 entries
		(searchable === "auto" && items.length > 7);

	// the clear button is displayed when:
	const isShowingClearButton =
		// the hideClearButton prop is set to false
		!hideClearButton &&
		// the icon-only mode is disabled
		!iconOnly &&
		// and there're selected options
		selectedOptions.length > 0 &&
		// the selected option is not the default one
		!isDefaultOptionSelected;

	// full-width mode is enabled when:
	const isShowingFullWidth =
		// the fullWidth prop is true
		fullWidth &&
		// and icon-only mode is disabled
		!iconOnly;

	// the listbox is visible when:
	const isShowingListbox =
		// it's possible to create options
		creatable ||
		// or there're items to show
		filteredItems.length > 0;

	// the "no matches" message is visible when:
	const isShowingNoMatches =
		// the loading state is false
		!loading &&
		// and it's not possible to create options
		!creatable &&
		// and there're no items to show
		filteredItems.length === 0;

	// the bulk actions are available when:
	const isShowingBulkActions =
		// the user sets the bulkActions prop to true
		bulkActions &&
		// and it's in multi-selection mode
		multiple &&
		// and searching is enabled
		isSearchingEnabled &&
		// and there're items to interact with
		filteredItems.length > 0;

	// the action bar with Confirm and Cancel buttons is visible when:
	const isShowingActionBar =
		// it's in multi-selection mode
		multiple;

	// in single-select mode, if no option is selected, selects the default option
	// if defaultOption prop is undefined, set it to the first option
	useEffect(() => {
		const shouldSelectDefaultOption = !multiple && items.length && innerValue === undefined;
		if (shouldSelectDefaultOption && defaultOption !== undefined) {
			const option: OptionItem<T> | undefined = defaultOption;
			option !== undefined && handleOnChange(selectionManager.toggleOption(option));
		}
	}, [items, selectedOptions, defaultOption]);

	const [popoverWidth, setPopoverWidth] = useState(listboxWidth);
	useEffect(() => {
		window.addEventListener("resize", onResize);
		onResize();
		return () => {
			window.removeEventListener("resize", onResize);
		};
	}, [onResize]);

	return (
		<div
			className={cn(className, isShowingFullWidth && scss.fullWidth)}
			data-component="select"
			data-observe-key={props["data-observe-key"]}
			onBlur={onBlur}
			onFocus={onFocus}
			onKeyDown={onKeyDown}
			onMouseDown={onMouseDown}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			style={{ width: buttonWidth, ...style }}
			tabIndex={tabIndex}
		>
			<Popover
				allowedAutoPlacements={allowedAutoPlacements}
				placement={placement}
				buttonRef={buttonRef}
				noMaxWidth={noListboxMaxWidth}
				noMinWidth={noListboxMinWidth}
				aria-label={props["aria-label"]}
				containerClassName={cn(scss.popoverContainer)}
				disabled={disabled}
				popoverContent={renderPopoverContent}
				data-observe-key={dataObserveKeyDiscriminator(props["data-observe-key"], "Select")}
				buttonProps={{
					"aria-describedby": props["aria-describedby"],
					"aria-labelledby": props["aria-labelledby"],
					"aria-haspopup": "listbox",
					"data-observe-key": dataObserveKeyDiscriminator(
						props["data-observe-key"],
						"Select",
						"Button"
					),
					fullWidth: isShowingFullWidth,
					id,
					...buttonProps,
				}}
				buttonContent={
					buttonRenderer !== undefined ? (
						multiple ? (
							(buttonRenderer as (x: OptionItem<T>[]) => React.ReactNode)?.(selectedOptions)
						) : (
							(buttonRenderer as (x: OptionItem<T> | undefined) => React.ReactNode)?.(
								selectedOptions[0]
							)
						)
					) : iconOnly ? (
						<Icon size="medium">
							<IconOptions />
						</Icon>
					) : (
						<PopoverButton
							disabled={disabled}
							showBadge={!!multiple}
							badgeCount={selectedOptions.length}
							buttonText={
								// in single-selection mode, display the
								// selected option title as the Select placeholder
								!multiple && selectedOptions.length === 1 ? selectedOptions[0]?.title : placeholder
							}
							icon={
								// in single-selection mode, display the
								// selected option icon
								!multiple && selectedOptions.length === 1 ? selectedOptions[0]?.icon : undefined
							}
							maxNumberOfItems={maxNumberOfItems}
						/>
					)
				}
				onClear={isShowingClearButton ? clearSelection : undefined}
				onClose={() => handleOnClose(isShowingActionBar)}
				hideChevron={iconOnly || hideChevron}
				invalid={invalid}
			/>
			{multiple && showSelectionOverview && (
				<SelectOverviewList
					compareFn={compareFn}
					id={overviewId}
					items={items}
					disabled={disabled}
					label={overviewLabel ?? i18nLab.selectedItems}
					onDeselect={(option) => {
						const newSelection = handleOnChange(selectionManager.deselectBulk([option]));
						confirmSelection(newSelection);
					}}
					optionRenderer={overviewOptionRenderer}
					value={initialSelection}
					fullWidth={isShowingFullWidth}
					data-observe-key={dataObserveKeyDiscriminator(
						props["data-observe-key"],
						"Select",
						"Overview"
					)}
				/>
			)}
		</div>
	);

	function renderPopoverContent(
		id: string,
		firstFocusableRef: React.RefObject<HTMLInputElement>,
		openState: { isOpen: boolean; setIsOpen: (isOpen: boolean) => void },
		trigger?: Trigger
	): JSX.Element {
		return (
			<div
				id={id}
				className={cn(
					scss.popoverContent,
					"fancy-SelectPopoverContent",
					noListboxMinWidth && scss.noMinWidth,
					noListboxMaxWidth && scss.noMaxWidth
				)}
				style={{ width: popoverWidth }}
			>
				{isSearchingEnabled && (
					<>
						<div className={cn(scss.searchInputField)}>
							<InputField
								aria-autocomplete="list"
								aria-controls={listboxId}
								aria-label={searchLabel}
								autoFocus
								fullWidth
								onChange={setSearchQuery}
								placeholder={searchPlaceholder ?? i18nLab.search}
								type="search"
								ref={firstFocusableRef}
								value={searchQuery}
								data-observe-key={dataObserveKeyDiscriminator(
									props["data-observe-key"],
									"Select",
									"SearchInput"
								)}
							/>
						</div>
						{/* show searchHelpText if the user has not searched, or if we are in 'async-search-mode' */}
						{searchHelpText && (searchQuery.length === 0 || onSearch) && (
							<TextContainer className={cn(scss.searchHelpText)}>
								{typeof searchHelpText === "string" ? (
									<InlineText size="small" tone="subtle">
										{searchHelpText}
									</InlineText>
								) : (
									searchHelpText
								)}
							</TextContainer>
						)}
						<Divider />
					</>
				)}
				{isShowingListbox && (
					<SelectListbox
						className="fancy-SelectListbox"
						caseSensitive={caseSensitive}
						compareFn={compareFn}
						createButtonLabel={createButtonLabel}
						firstOptionRef={
							isSearchingEnabled || trigger !== Trigger.Keyboard ? undefined : firstFocusableRef
						}
						heading={listboxHeading}
						id={listboxId}
						items={filteredItems}
						multiple={multiple}
						needle={searchQuery}
						onToggleOption={(option) => {
							handleOnChange(selectionManager.toggleOption(option));
							if (!multiple) openState.setIsOpen(false);
							setScreenReadFeedback("");
						}}
						onSelectBulk={(options) => {
							handleOnChange(selectionManager.selectBulk(options));
							if (!multiple) openState.setIsOpen(false);
							setScreenReadFeedback("");
						}}
						onDeselectBulk={(options) => {
							handleOnChange(selectionManager.deselectBulk(options));
							if (!multiple) openState.setIsOpen(false);
							setScreenReadFeedback("");
						}}
						onCreate={async (value) => {
							const newOption = await handleOnCreate(value);
							if (newOption) setLastCreatedOption(newOption);
							if (!multiple || newOption === false) openState.setIsOpen(false);
							setScreenReadFeedback("");
						}}
						optionRenderer={optionRenderer}
						showCreateButton={creatable && searchQuery.length !== 0 && !searchQueryExactlyMatches}
						showSelectedItemsOnTop={showSelectedItemsOnTop}
						reRenderListbox={reRenderListbox}
						value={selectedOptions}
						maxNumberOfItems={maxNumberOfItems}
						{...rest}
					/>
				)}
				{loading && (
					<div className={cn(scss.spinnerContainer)}>
						<Spinner variant="dark" size="medium" />
					</div>
				)}
				{isShowingNoMatches && (
					<Paragraph tone="subtle" alignment="center" className={scss.noMatchesMessage}>
						{i18nLab.noMatches}
					</Paragraph>
				)}
				{isShowingBulkActions && (
					<>
						<Divider />
						<Button.Group className={cn(scss.bulkActions)}>
							<SelectAllButton
								disabled={
									// if its loading
									loading ||
									// or if either the items or the filtered items lists are empty
									flatFilteredOptions.length === 0 ||
									flatOptions.length === 0 ||
									// or if all items are already selected
									flatFilteredOptions.every(
										(option) => !selectionManager.isEligibleToSelectAll(option)
									)
								}
								filteredOptions={flatFilteredOptions}
								hasSearchQuery={searchQuery.length > 0}
								selectAllEligibleFn={selectionManager.isEligibleToSelectAll}
								compareFn={compareFn}
								onClick={(updatedSelection: OptionItem<T>[]) => {
									handleOnChange(selectionManager.selectBulk(updatedSelection));
									setScreenReadFeedback(`${i18nLab.selectedItems}: ${updatedSelection.length}`);
								}}
								data-observe-key={dataObserveKeyDiscriminator(
									props["data-observe-key"],
									"Select",
									"SelectAllButton"
								)}
							/>
							<DeselectAllButton
								disabled={loading}
								filteredOptions={flatFilteredOptions}
								selectedOptions={selectedOptions}
								compareFn={compareFn}
								onClick={(updatedSelection: OptionItem<T>[]) => {
									handleOnChange(selectionManager.deselectBulk(updatedSelection));
									setScreenReadFeedback(`${i18nLab.selectedItems}: 0`);
								}}
								data-observe-key={dataObserveKeyDiscriminator(
									props["data-observe-key"],
									"Select",
									"DeselectAllButton"
								)}
							/>
						</Button.Group>
						<SrOnly status>{screenReadFeedback}</SrOnly>
					</>
				)}
				{isShowingActionBar && (
					<>
						<Divider />
						<ActionBar
							primary={{
								children: i18nLib.confirm,
								onClick: () => {
									confirmSelection();
									openState.setIsOpen(false);
								},
								"data-observe-key": dataObserveKeyDiscriminator(
									props["data-observe-key"],
									"Select",
									"ConfirmButton"
								),
								disabled: selectionManager.stateCompareFn(selectedOptions, initialSelection),
							}}
							cancel={{
								children: i18nLib.cancel,
								onClick: () => {
									handleOnClose(true);
									openState.setIsOpen(false);
								},
								"data-observe-key": dataObserveKeyDiscriminator(
									props["data-observe-key"],
									"Select",
									"CancelButton"
								),
							}}
						/>
					</>
				)}
			</div>
		);
	}

	function onResize() {
		if (buttonWidth === undefined && isShowingFullWidth && !listboxWidth && buttonRef?.current) {
			setPopoverWidth(buttonRef.current.clientWidth);
		}
	}

	function onChangeWrapper(selection: OptionItem<T>[]) {
		// publish the new output value
		onChange(
			(multiple ? selection.map((option) => option.value) : selection[0]?.value) as SelectValue<T>
		);

		// flush the inner value state
		setInnerValue(value);
	}

	function clearSelection() {
		const newSelection = selectionManager.clearSelection();
		confirmSelection(newSelection);
		setPristine(false);
	}

	function confirmSelection(selection: OptionItem<T>[] = selectedOptions) {
		setInitialSelection([...selection]);
		setSearchQuery("");

		// publish the new output value
		onChangeWrapper(selection);
	}

	function cancelSelection() {
		const newValue = multiple
			? initialSelection.map((option) => option.value)
			: initialSelection[0]?.value;
		setInnerValue(newValue as SelectValue<T>);
		selectionManager.onCancel?.();
	}

	function handleOnClose(shouldCancelSelection: boolean) {
		shouldCancelSelection && cancelSelection();
		setSearchQuery("");
	}

	async function handleOnCreate(value: string): Promise<OptionItem<T> | false | undefined> {
		const res = await onCreate?.(value);
		setSearchQuery("");
		setPristine(false);
		return res;
	}

	function handleOnChange(newSelection: OptionItem<T>[]): OptionItem<T>[] {
		// if the new selection does not violeate the max constrain, do not
		// update the selection
		if (maxNumberOfItems && newSelection.length > maxNumberOfItems) return selectedOptions;

		// update the inner value with the new selection
		const newValue = multiple ? newSelection.map((option) => option.value) : newSelection[0]?.value;
		setInnerValue(newValue as SelectValue<T>);
		setPristine(false);

		// if the confirm selection button is not being displayed,
		// so confirm it automaticaly
		if (!isShowingActionBar) confirmSelection(newSelection);

		return newSelection;
	}
}

type SelectAllButtonProps<T> = {
	onClick: (updatedSelection: OptionItem<T>[]) => void;
	filteredOptions: OptionItem<T>[];
	hasSearchQuery: boolean;
	disabled?: boolean;
	compareFn?: CompareFunction<T>;
	selectAllEligibleFn: (option: OptionItem<T>) => boolean;
} & DataObserveKey;

function SelectAllButton<T>(props: SelectAllButtonProps<T>): JSX.Element {
	const {
		disabled,
		onClick,
		hasSearchQuery,
		filteredOptions,
		selectAllEligibleFn,
		compareFn,
		...rest
	} = props;
	const itemsToSelect: OptionItem<T>[] = filteredOptions.filter((x) => selectAllEligibleFn(x));
	const i18n = useLabTranslations();

	return (
		<Button size="small" disabled={disabled} onClick={() => onClick(itemsToSelect)} {...rest}>
			{hasSearchQuery ? i18n.selectAllMatching : i18n.selectAll}
			{`(${itemsToSelect.length})`}
		</Button>
	);
}

type DeselectAllButtonProps<T> = {
	onClick: (updatedSelection: OptionItem<T>[]) => void;
	filteredOptions: OptionItem<T>[];
	selectedOptions: OptionItem<T>[];
	disabled?: boolean;
	compareFn?: CompareFunction<T>;
} & DataObserveKey;

function DeselectAllButton<T>(props: DeselectAllButtonProps<T>): JSX.Element {
	const { disabled, onClick, filteredOptions, selectedOptions, compareFn, ...rest } = props;
	const itemsToDeselect: OptionItem<T>[] = filteredOptions.filter(
		(x) => indexOfOption(selectedOptions, x, compareFn) !== -1
	);

	const i18n = useLabTranslations();

	return (
		<Button
			size="small"
			disabled={
				// if its disabled property is true
				disabled ||
				// or there's nothing to deselect
				itemsToDeselect.length === 0
			}
			onClick={() => onClick(itemsToDeselect)}
			{...rest}
		>
			{`${i18n.deselectAll}${itemsToDeselect.length > 0 ? ` (${itemsToDeselect.length})` : ""}`}
		</Button>
	);
}

type PopoverButtonProps = {
	showBadge: boolean;
	buttonText?: string;
	badgeCount: number;
	disabled?: boolean;
	maxNumberOfItems?: number;
	icon?: React.ReactNode;
};

function PopoverButton(props: PopoverButtonProps): JSX.Element {
	const { badgeCount, buttonText, showBadge, disabled, icon, maxNumberOfItems } = props;
	const i18n = useLabTranslations();
	return (
		<div className={cn(scss.popoverButton)}>
			{icon}
			<InlineText tone={disabled ? "subtle" : undefined} className={cn(scss.popoverButtonText)}>
				{showBadge && badgeCount > 0 ? (
					<>
						<Badge size="small" className={cn(scss.badge)} type={disabled ? "subtle" : undefined}>
							{`${badgeCount}${maxNumberOfItems ? ` / ${maxNumberOfItems}` : ""}`}
						</Badge>
						{i18n.selected}
					</>
				) : (
					<>
						{buttonText ?? i18n.select}
						{maxNumberOfItems && (
							<InlineText size="xSmall" tone="subtle">
								&nbsp;({i18n.max}: {maxNumberOfItems})
							</InlineText>
						)}
					</>
				)}
			</InlineText>
		</div>
	);
}

function getFirstOption<T>(items: SelectItem<T>[]): OptionItem<T> | null {
	for (const item of items) {
		if (isOptionItem(item)) return item;
		if (item.items.length) return item.items[0];
	}
	return null;
}

export function useCachedSelection<T>(
	options: OptionItem<T>[],
	value: SelectValue<T>,
	compareFn: CompareFunction<T> | undefined
): [selectedOptions: OptionItem<T>[], allOptions: OptionItem<T>[]] {
	const selectionCache = useRef<OptionItem<T>[]>([...options]);

	const allOptions = useMemo<OptionItem<T>[]>(
		() => [...options, ...selectionCache.current].filter((x) => x.value !== undefined),
		[selectionCache, options]
	);

	const selectedOptions = useMemo<OptionItem<T>[]>(
		() =>
			(value === undefined ? [] : Array.isArray(value) ? value : [value])
				.map((option) =>
					allOptions.find((x) => (compareFn ? compareFn(x.value, option) : x.value === option))
				)
				.filter((option) => option !== undefined) as OptionItem<T>[],
		[value, allOptions]
	);

	selectionCache.current = [...selectedOptions];

	return [selectedOptions, allOptions];
}

export function useDefaultOption<T>(
	props: SelectProps<T>,
	selectedOptions: OptionItem<T>[],
	allOptions: OptionItem<T>[]
): [defaultOption: OptionItem<T> | undefined, isSelected: boolean] {
	const { compareFn, defaultOption: userDefaultValue, items = [], noDefaultOption } = props;

	const multiple = Array.isArray(props.value);

	const defaultValue: SelectDto<T> | undefined = noDefaultOption
		? undefined
		: userDefaultValue === undefined
		? getFirstOption(items)?.value
		: userDefaultValue;

	const defaultOption: OptionItem<T> | undefined =
		defaultValue !== undefined
			? allOptions.find((option) =>
					compareFn ? compareFn(option.value, defaultValue) : option.value === defaultValue
			  )
			: undefined;

	const isSelected =
		!multiple &&
		defaultValue !== undefined &&
		isOptionEqual(selectedOptions[0], defaultOption, compareFn);

	return [defaultOption, isSelected];
}

export function useFilteredOptions<T>(
	items: SelectItem<T>[],
	searchQuery: string,
	caseSensitive?: boolean,
	searchableContent?: (item: OptionItem<T>) => string,
	onSearch?: (query: string, caseSensitive: boolean) => void
): [filteredItems: SelectItem<T>[], exactlyMatches: boolean] {
	const [filteredItems, setFilteredItems] = useState<SelectItem<T>[]>(items);

	useEffect(() => {
		onSearch !== undefined
			? onSearch(searchQuery, !!caseSensitive)
			: setFilteredItems(handleSearch(items, searchQuery, !!caseSensitive, searchableContent));
	}, [searchQuery, searchableContent]);

	useEffect(() => {
		setFilteredItems(
			onSearch !== undefined
				? items
				: handleSearch(items, searchQuery, !!caseSensitive, searchableContent)
		);
	}, [items]);

	const titleExactlyMatchesSearchQuery = filteredItems.some((item) => {
		if (isOptionItem(item)) {
			return titleExactlyMatches(item, searchQuery, !!caseSensitive);
		}
		return item.items.some((item) => titleExactlyMatches(item, searchQuery, !!caseSensitive));
	});

	return [filteredItems, titleExactlyMatchesSearchQuery];
}

function handleSearch<T>(
	items: SelectItem<T>[],
	searchQuery: string,
	caseSensitive: boolean,
	searchableContent?: (item: OptionItem<T>) => string
): SelectItem<T>[] {
	if (searchQuery.length === 0) return items;

	const query = caseSensitive ? searchQuery : searchQuery.toLowerCase();

	// if no searchableContent function is provided, apply search to
	// options title
	const defaultSearchableContent = (option: OptionItem<T>) => option.title;
	const filter = (option: OptionItem<T>) => {
		const rawTextContext = searchableContent?.(option) ?? defaultSearchableContent(option);
		const textContext: string = caseSensitive ? rawTextContext : rawTextContext.toLowerCase();
		return textContext.includes(query);
	};
	let filteredItems: SelectItem<T>[];
	// filter options
	filteredItems = items.filter((item) => {
		if (isOptionItem(item)) return filter(item);
		return true;
	});
	// filter options inside groups
	filteredItems = filteredItems.map((item) => {
		if (isOptionItem(item)) return item;
		const filterredGroupOption = item.items.filter(filter);
		return { ...item, items: filterredGroupOption };
	});
	return filteredItems;
}

function titleExactlyMatches<T>(
	option: OptionItem<T>,
	searchQuery: string,
	caseSensitive: boolean
): boolean {
	const query = caseSensitive ? searchQuery : searchQuery.toLowerCase();
	const title = caseSensitive ? option.title : option.title.toLowerCase();
	return query === title;
}

export type SelectStateManager<T> = {
	/** Toggles the option selection state */
	toggleOption: (option: OptionItem<T>) => OptionItem<T>[];
	/** Select a set of options */
	selectBulk: (options: OptionItem<T>[]) => OptionItem<T>[];
	/** Deselect a set of options */
	deselectBulk: (options: OptionItem<T>[]) => OptionItem<T>[];
	/** Clear all selections */
	clearSelection: () => OptionItem<T>[];
	/** Function that checks if an option is eligible to be selected by the Select All button. */
	isEligibleToSelectAll: (option: OptionItem<T>) => boolean;
	/** Function that return true is two selection states are equal */
	stateCompareFn: (set1: OptionItem<T>[], b: OptionItem<T>[]) => boolean;
	/** Callback for when the changes on the selection are canceled. One may use it together with the updateSelectionFn to control the selection. */
	onCancel?: () => void;
};

export function useSelectStateManager<T>(
	currentSelection: OptionItem<T>[],
	multiple: boolean | undefined,
	compareFn?: CompareFunction<T>,
	defaultOption?: OptionItem<T>
): SelectStateManager<T> {
	const toggleOption = useCallback(
		(option: OptionItem<T>) => {
			if (!multiple) {
				return [option];
			}
			const updatedSelectedOptions = [...currentSelection];
			const targetIndex = indexOfOption(updatedSelectedOptions, option, compareFn);
			if (targetIndex !== -1) {
				// remove item from list if it's already selected
				updatedSelectedOptions.splice(targetIndex, 1);
			} else {
				// append item to the list if it's not selected
				updatedSelectedOptions.push(option);
			}
			return updatedSelectedOptions;
		},
		[currentSelection, multiple, compareFn]
	);

	const selectBulk = useCallback(
		(options: OptionItem<T>[]) => {
			const updatedSelectedOptions = [...currentSelection];
			options.forEach((option) => {
				const targetIndex = indexOfOption(updatedSelectedOptions, option, compareFn);
				if (targetIndex === -1) {
					updatedSelectedOptions.push(option);
				}
			});
			return updatedSelectedOptions;
		},
		[currentSelection, multiple, compareFn]
	);

	const deselectBulk = useCallback(
		(options: OptionItem<T>[]) => {
			const updatedSelectedOptions = [...currentSelection];
			options.forEach((option) => {
				const targetIndex = indexOfOption(updatedSelectedOptions, option, compareFn);
				if (targetIndex !== -1) {
					updatedSelectedOptions.splice(targetIndex, 1);
				}
			});
			return updatedSelectedOptions;
		},
		[currentSelection, multiple, compareFn]
	);

	const clearSelection = useCallback(() => {
		return !multiple && defaultOption !== undefined ? [defaultOption] : [];
	}, [multiple, defaultOption]);

	const isEligibleToSelectAll = useCallback(
		(option: OptionItem<T>) => {
			return indexOfOption(currentSelection, option, compareFn) === -1 && !option.disabled;
		},
		[currentSelection, multiple, compareFn]
	);

	const stateCompareFn = useCallback(
		(set1: OptionItem<T>[], set2: OptionItem<T>[]) => areOptionsEqual(set1, set2, compareFn),
		[compareFn]
	);

	return {
		toggleOption,
		selectBulk,
		deselectBulk,
		clearSelection,
		isEligibleToSelectAll,
		stateCompareFn,
	};
}
